import React, { useState, useEffect } from "react";
import styles from "../assets/css/UserList.module.css";
import common from "../assets/css/common.module.css";
import { Loader } from "../../components/Loader";
import { postData1 } from "../../services";
import { Pagination, DownloadToExcel } from "../Common";
import { orderStatus } from "../MappedData";
import { useNavigate } from "react-router-dom";

// Backend payment_type choices (1-10) — see order-management-implementation-plan-updated.md
const paymentTypeMap = {
  1: "Cash",
  2: "Bank Transfer",
  3: "Credit Card",
  4: "Debit Card",
  5: "UPI",
  6: "Wallet",
  7: "Cheque",
  8: "Net Banking",
  9: "COD",
  10: "Other",
};

// Backend payment_status choices (1-3)
const paymentStatusMap = {
  1: { label: "Unpaid", cls: "bg-gray-100 text-gray-600 border-gray-200" },
  2: { label: "Partial", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  3: { label: "Paid", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
};

// Offline order detection — the backend has no dedicated order_source field yet,
// so detect via any field the admin create-order flow sends / the serializer returns
// (order_source, others.created_offline, or a non-empty created_by for admin-made orders).
const getOrderSource = (data) => {
  if (
    data?.order_source === "admin_offline" ||
    data?.order_source === "offline" ||
    data?.others?.created_offline === true ||
    data?.others?.created_offline === "true" ||
    data?.created_by
  ) {
    return "Offline";
  }
  return "Online";
};

export const OrderList = () => {
  const navigate = useNavigate();
  const [orderlistData, setOrderListData] = useState([]);
  const [sortedData, setSortedData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [currPage, setCurrPage] = useState(1);
  const [perPage, setPerPage] = useState(8);
  const [paging, setPaging] = useState({});
  const [prevPage, setPrevPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  // Fetch Orders
  useEffect(() => {
    (async () => {
      const config = { _page: currPage, _limit: perPage };
      const res = await postData1("admin/order_list/", config, true, true);
      // console.log("Order List Response:", res?.data?.data);
      setOrderListData(res?.data?.data || []);
      setPaging({
        total: res?.data?.total,
        total_pages: res?.data?.total_pages,
      });
      setLoader(false);
    })();
  }, [currPage, perPage]);

  // Handle Sorting
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });

    const sortedArray = [...orderlistData].sort((a, b) => {
      let valueA = a[key];
      let valueB = b[key];

      // Handle nested values
      if (key === "product") {
        valueA = a?.product_detail?.equip_name || "";
        valueB = b?.product_detail?.equip_name || "";
      } else if (key === "order_date") {
        valueA = new Date(a?.created_date).getTime();
        valueB = new Date(b?.created_date).getTime();
      } else if (key === "status") {
        valueA = orderStatus[a?.order_status] || "";
        valueB = orderStatus[b?.order_status] || "";
      } else if (key === "payment_type") {
        valueA = paymentTypeMap[a?.payment_type] || "";
        valueB = paymentTypeMap[b?.payment_type] || "";
      } else if (key === "payment_status") {
        valueA = paymentStatusMap[a?.payment_status]?.label || "";
        valueB = paymentStatusMap[b?.payment_status]?.label || "";
      } else if (key === "source") {
        valueA = getOrderSource(a);
        valueB = getOrderSource(b);
      }

      if (valueA < valueB) return direction === "asc" ? -1 : 1;
      if (valueA > valueB) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setSortedData(sortedArray);
  };

  useEffect(() => {
    setSortedData(orderlistData);
  }, [orderlistData]);

  // Update Pagination
  const updatePage = (e) => {
    let name = e.currentTarget.name;
    if (name === "prev") {
      setCurrPage(currPage === 1 ? 1 : currPage - 1);
      setPrevPage(currPage);
    } else if (name === "next") {
      setCurrPage(currPage === paging?.total_pages ? currPage : currPage + 1);
      setPrevPage(currPage);
    } else {
      setCurrPage(parseInt(e.currentTarget.innerHTML));
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle View Details
  const handleView = (e, data) => {
    navigate("/admin/manageorder/view/", { state: data });
  };

  const handleInvoice = (e, data) => {
    navigate("/admin/invoice/", { state: data });
  };

  const excelData = sortedData.map((data) => ({
    "Order ID": data?.id,
    Product: data?.product_detail?.equip_name || "",
    "Order Date": data?.created_date ? new Date(data.created_date).toLocaleDateString() : "",
    Buyer: data?.buyer_firstname || "",
    "buyer_phone": data?.buyer_phone || "",
    "buyer_email": data?.buyer_email || "",
    Seller: data?.seller_firstname || "",
    Status: orderStatus[data?.order_status] || "",
    Quantity: data?.quantity || "",
    "Payment Type": paymentTypeMap[data?.payment_type] || "",
    "Payment Status": paymentStatusMap[data?.payment_status]?.label || "",
    "Remain Amount": data?.remain_amount ?? "",
    Source: getOrderSource(data),
  }));

  return (
    <section className={styles.userListCont}>
      <div className={styles.listHead}>
        <h1>ORDER MANAGEMENT</h1>
        <div className="ml-auto w-8">
          {/* <DownloadToExcel data={excelData} filename={`orders-page-${currPage}`} /> */}
        </div>
      </div>
      <div className={styles.dataItems}>
        <div className={styles.itemHead}>
          <span onClick={() => handleSort("id")} style={{ cursor: "pointer" }}>
            ORDER ID {sortConfig.key === "id" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </span>
          <span onClick={() => handleSort("product")} style={{ cursor: "pointer" }}>
            PRODUCT {sortConfig.key === "product" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </span>
          <span onClick={() => handleSort("order_date")} style={{ cursor: "pointer" }}>
            ORDER DATE {sortConfig.key === "order_date" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </span>
          <span onClick={() => handleSort("buyer_firstname")} style={{ cursor: "pointer" }}>
            BUYER {sortConfig.key === "buyer_firstname" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </span>
          <span onClick={() => handleSort("buyer_firstname")} style={{ cursor: "pointer" }}>
            BUYER Mob {sortConfig.key === "buyer_firstname" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </span>
          <span onClick={() => handleSort("seller_firstname")} style={{ cursor: "pointer" }}>
            SELLER {sortConfig.key === "seller_firstname" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </span>
          <span onClick={() => handleSort("status")} style={{ cursor: "pointer" }}>
            STATUS {sortConfig.key === "status" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </span>
          <span onClick={() => handleSort("quantity")} style={{ cursor: "pointer" }}>
            QUANTITY {sortConfig.key === "quantity" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </span>
          <span onClick={() => handleSort("payment_type")} style={{ cursor: "pointer" }}>
            PAY TYPE {sortConfig.key === "payment_type" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </span>
          <span onClick={() => handleSort("payment_status")} style={{ cursor: "pointer" }}>
            PAY STATUS {sortConfig.key === "payment_status" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </span>
          <span onClick={() => handleSort("remain_amount")} style={{ cursor: "pointer" }}>
            TOTAL {sortConfig.key === "remain_amount" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </span>
          <span onClick={() => handleSort("remain_amount")} style={{ cursor: "pointer" }}>
            REMAIN AMOUNT {sortConfig.key === "remain_amount" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </span>
          <span onClick={() => handleSort("source")} style={{ cursor: "pointer" }}>
            SOURCE {sortConfig.key === "source" ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
          </span>
          <span>Actions</span>
        </div>

        {sortedData.map((data, index) => (
          // console.log("Order Data:", data),
          <div className={styles.itemData} key={index}>

            {/* <span>{data?.id}</span> */}
            <span>{data?.order_id}</span>
            <span>{data?.product_detail?.equip_name}</span>
            <span>{new Date(data?.created_date).toLocaleDateString()}</span>
            <span>{data?.buyer_firstname}</span>
             <span>{data?.buyer_phone}</span>
            <span>{data?.seller_firstname}</span>
           
            <span>
              <span className={common.customStyle}>
                {orderStatus[data?.order_status]}
              </span>
            </span>
            <span>{data?.quantity}</span>
            <span>{paymentTypeMap[data?.payment_type] || "—"}</span>
            <span>
              <span
                className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-semibold ${paymentStatusMap[data?.payment_status]?.cls ||
                  "bg-gray-100 text-gray-600 border-gray-200"
                  }`}
              >
                {paymentStatusMap[data?.payment_status]?.label || "—"}
              </span>
            </span>
            <span>
              {data?.total != null && data?.total !== ""
                ? `₹${Number(data?.total).toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}`
                : "—"}
            </span>
            <span>
              {data?.remain_amount != null && data?.remain_amount !== ""
                ? `₹${Number(data?.remain_amount).toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}`
                : "—"}
            </span>
            <span>
              <span
                className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-semibold ${data.others.payment_mode === "offline"
                  ? "bg-violet-100 text-violet-700 border-violet-200"
                  : "bg-sky-100 text-sky-700 border-sky-200"
                  }`}
              >
                {data.others.payment_mode}
              </span>
            </span>
            <div className="flex items-center justify-between gap-2">
              <span onClick={(e) => handleView(e, data)} style={{ cursor: "pointer" }}>
                View
              </span>
              <span onClick={(e) => handleInvoice(e, data)} style={{ cursor: "pointer" }}>
                Invoice
              </span>
            </div>
          </div>
        ))}
      </div>

      <Pagination
        total={paging?.total}
        perPage={perPage}
        setPerPage={setPerPage}
        currPage={currPage}
        setCurrPage={setCurrPage}
        prevPage={prevPage}
        tPage={paging?.total_pages}
        updatePage={updatePage}
      />

      {loader && <Loader />}
    </section>
  );
};
